// frontend/src/components/payroll/PayStub/index.tsx
// Novala canonical pay stub component (STACKED FULL-WIDTH LAYOUT).
// Letter proportion (816 x 1056), Inter, tabular numerals.

import React from "react";
import type { PayStubData } from "./types";
import { money, num, formatDate } from "./helpers";
import {
  styles,
  TEAL,
  INK,
  MUTED,
} from "./styles";

interface PayStubProps {
  data?: PayStubData | null;
}

export default function PayStub({ data }: PayStubProps): React.ReactElement | null {
  if (!data) return null;

  const employer = data.employer || {};
  const employerName = employer.name || data.employer_name || "";
  const employerStreet = employer.address_street || "";
  const employerCityLine = [
    employer.address_city,
    employer.address_province,
    employer.address_postal_code,
  ].filter(Boolean).join(" ");

  const empName = data.employee_name || "";
  const empAddrLines = String(data.employee_address || "").split("\n").filter(Boolean);
  const empLine1 = empAddrLines[0] || "";
  const empLine2 = empAddrLines[1] || "";

  const periodBeginning = formatDate(data.pay_period_start);
  const periodEnding = formatDate(data.pay_period_end);
  const payDate = formatDate(data.pay_date);

  const payLines = (data.pay && data.pay.lines) || [];
  const taxLines = (data.employee_taxes && data.employee_taxes.lines) || [];
  const deductLines = (data.deductions_contributions && data.deductions_contributions.lines) || [];

  const payTotal = (data.pay && data.pay.total) || { current: 0, ytd: 0 };
  const taxTotal = (data.employee_taxes && data.employee_taxes.total) || { current: 0, ytd: 0 };
  const deductTotal = (data.deductions_contributions && data.deductions_contributions.total) || { current: 0, ytd: 0 };

  const netPay = data.net_pay;
  const memo = data.memo || "";
  const hasMemo = String(memo).trim().length > 0;

  const method = (data.payment_method || data.pay_method || "").toLowerCase();
  const isCheque = method.includes("cheque") || method.includes("check");
  const chequeNo = data.cheque_number;

  return (
    <div style={styles.paper}>
      {/* Teal bar top */}
      <div style={styles.bar} />

      <div style={styles.content}>
        {/* Three column header */}
        <div style={styles.headerRow}>
          <div>
            <div style={styles.columnLabel}>Employer</div>
            <div style={styles.bold}>{employerName}</div>
            {employerStreet && <div style={styles.muted}>{employerStreet}</div>}
            {employerCityLine && <div style={styles.muted}>{employerCityLine}</div>}
          </div>
          <div>
            <div style={styles.columnLabel}>Pay period</div>
            <div style={{ fontSize: 13, color: INK, marginBottom: 4 }}>
              {periodBeginning && periodEnding
                ? periodBeginning + " - " + periodEnding
                : (periodBeginning || periodEnding || "")}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 5 }}>
              Pay date: <strong style={{ color: INK }}>{payDate}</strong>
            </div>
          </div>
          <div>
            <div style={styles.columnLabel}>Employee</div>
            <div style={styles.bold}>{empName}</div>
            {empLine1 && <div style={styles.muted}>{empLine1}</div>}
            {empLine2 && <div style={styles.muted}>{empLine2}</div>}
          </div>
        </div>

        <div style={styles.dashed} />

        {/* PAY table - full width */}
        {payLines.length > 0 && (
          <div>
            <div style={styles.sectionLabel}>Pay</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.thRight}>Hours</th>
                  <th style={styles.thRight}>Rate</th>
                  <th style={styles.thRight}>Current</th>
                  <th style={styles.thRight}>YTD</th>
                </tr>
              </thead>
              <tbody>
                {payLines.map((l, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{l.label || l.type || ""}</td>
                    <td style={styles.tdRight}>{l.hours != null ? num(l.hours) : ""}</td>
                    <td style={styles.tdRight}>{l.rate != null ? money(l.rate, true) : ""}</td>
                    <td style={styles.tdRight}>{money(l.current, true)}</td>
                    <td style={styles.tdRight}>{money(l.ytd, true)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.totalCell}>Total gross</td>
                  <td></td>
                  <td></td>
                  <td style={styles.totalCellRight}>{money(payTotal.current, true)}</td>
                  <td style={styles.totalCellRight}>{money(payTotal.ytd, true)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* TAXES table - full width */}
        {taxLines.length > 0 && (
          <div>
            <div style={styles.sectionLabel}>Taxes</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.thRight}>Current</th>
                  <th style={styles.thRight}>YTD</th>
                </tr>
              </thead>
              <tbody>
                {taxLines.map((l, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{l.label || l.type || ""}</td>
                    <td style={styles.tdRight}>{money(l.current, true)}</td>
                    <td style={styles.tdRight}>{money(l.ytd, true)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.totalCell}>Total taxes</td>
                  <td style={styles.totalCellRight}>{money(taxTotal.current, true)}</td>
                  <td style={styles.totalCellRight}>{money(taxTotal.ytd, true)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* DEDUCTIONS table - full width */}
        {deductLines.length > 0 && (
          <div>
            <div style={styles.sectionLabel}>Deductions</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.thRight}>Current</th>
                  <th style={styles.thRight}>YTD</th>
                </tr>
              </thead>
              <tbody>
                {deductLines.map((l, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{l.label || l.type || ""}</td>
                    <td style={styles.tdRight}>{money(l.current, true)}</td>
                    <td style={styles.tdRight}>{money(l.ytd, true)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.totalCell}>Total deductions</td>
                  <td style={styles.totalCellRight}>{money(deductTotal.current, true)}</td>
                  <td style={styles.totalCellRight}>{money(deductTotal.ytd, true)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* SUMMARY - full width */}
        <div style={styles.summaryBox}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Gross pay</span>
            <span style={styles.summaryValue}>{money(payTotal.current, true)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Less: total taxes</span>
            <span style={styles.summaryLabel}>-{money(taxTotal.current, true)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Less: total deductions</span>
            <span style={styles.summaryLabel}>-{money(deductTotal.current, true)}</span>
          </div>
          <div style={styles.summaryRowLast}>
            <span style={styles.netLabel}>Net pay</span>
            <span style={styles.netValue}>{money(netPay, true)}</span>
          </div>
        </div>

        {/* Payment method footer */}
        <div style={styles.footer}>
          Paid by{" "}
          <strong style={{ color: INK }}>
            {isCheque ? "Cheque" : "Direct deposit"}
          </strong>
          {isCheque && chequeNo ? (
            <span>
              {" "}&middot; Cheque no{" "}
              <strong style={{ color: INK }}>{chequeNo}</strong>
            </span>
          ) : null}
        </div>

        {/* Memo (if any) */}
        {hasMemo && (
          <div style={styles.memoBox}>
            <div style={styles.memoLabel}>Memo</div>
            <div style={styles.memoText}>{memo}</div>
          </div>
        )}
      </div>

      {/* Teal bar bottom */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 9, background: TEAL }} />
    </div>
  );
}